import React from 'react';
import { makeStyles, Box, Typography, Button } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import RocketLaunchIcon from '@material-ui/icons/FlightTakeoff';
import GridViewIcon from '@material-ui/icons/Apps';
import { useTokens } from '../../theme/useTokens';
import { dark, light } from '../../theme/tokens';
import { SparrowMark } from '../Root/SparrowMark';

const useStyles = makeStyles(theme => {
  const t = theme.palette.type === 'dark' ? dark : light;
  return {
    banner: {
      position: 'relative',
      overflow: 'hidden',
      background: t.primary50,
      border: `1px solid ${t.primaryBorder}`,
      borderRadius: 16,
      padding: theme.spacing(3.75, 4),
      marginBottom: theme.spacing(3.5),
    },
    watermark: {
      position: 'absolute',
      right: -20,
      top: -30,
      opacity: 0.1,
      pointerEvents: 'none',
      lineHeight: 0,
    },
    content: {
      position: 'relative',
      maxWidth: 660,
    },
    eyebrow: {
      fontSize: '0.6875rem',
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: t.primary700,
    },
    title: {
      margin: theme.spacing(1.25, 0, 1),
      fontSize: '1.875rem',
      lineHeight: 1.15,
      fontWeight: 800,
      letterSpacing: '-0.02em',
      color: t.text,
    },
    subtitle: {
      margin: 0,
      marginBottom: theme.spacing(2.5),
      fontSize: '0.9375rem',
      lineHeight: 1.55,
      color: t.textMuted,
    },
    buttonGroup: {
      display: 'flex',
      gap: theme.spacing(1.5),
      flexWrap: 'wrap',
    },
    primaryButton: {
      height: 40,
      padding: theme.spacing(0, 2.25),
      fontWeight: 600,
      fontSize: '0.875rem',
    },
    secondaryButton: {
      height: 40,
      padding: theme.spacing(0, 2.25),
      fontWeight: 600,
      fontSize: '0.875rem',
      backgroundColor: t.surface,
      borderColor: t.primaryBorder,
      color: t.primary700,
      '&:hover': {
        backgroundColor: t.surfaceSubtle,
        borderColor: t.primaryBorder,
      },
    },
  };
});

/** Extracts a friendly first name from a display name / email, if any. */
const firstNameFrom = (raw?: string): string | undefined => {
  if (!raw || raw.toLowerCase() === 'guest') return undefined;
  const local = raw.includes('@') ? raw.split('@')[0] : raw;
  const token = local.replace(/[._-]+/g, ' ').trim().split(' ')[0];
  if (!token) return undefined;
  return token.charAt(0).toUpperCase() + token.slice(1);
};

export const WelcomeBanner = () => {
  const classes = useStyles();
  const t = useTokens();
  const navigate = useNavigate();
  const identityApi = useApi(identityApiRef);

  const { value: name } = useAsync(async () => {
    const profile = await identityApi.getProfileInfo();
    return firstNameFrom(profile.displayName) ?? firstNameFrom(profile.email);
  }, [identityApi]);

  return (
    <Box className={classes.banner}>
      <Box className={classes.watermark}>
        <SparrowMark size={220} color={t.primary} />
      </Box>
      <Box className={classes.content}>
        <Typography className={classes.eyebrow}>
          Internal developer portal
        </Typography>
        <Typography variant="h1" className={classes.title}>
          {name ? `Welcome back, ${name}` : 'Welcome to Sparrow Launchpad'}
        </Typography>
        <Typography className={classes.subtitle}>
          Build, deploy, and manage software at scale. Scaffold new services,
          request infrastructure access, and keep your Kubernetes spend in
          check — all from one place.
        </Typography>
        <Box className={classes.buttonGroup}>
          <Button
            variant="contained"
            color="primary"
            className={classes.primaryButton}
            onClick={() => navigate('/create')}
            startIcon={<RocketLaunchIcon />}
          >
            Create service
          </Button>
          <Button
            variant="outlined"
            className={classes.secondaryButton}
            onClick={() => navigate('/catalog')}
            startIcon={<GridViewIcon />}
          >
            Explore catalog
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
